import { _ } from "../../../core/polyglot/polyglot.js";
import AbstractFormulaNode from "../../keyFigure/formula/AbstractFormulaNode.js";
import NaFormulaNode from "../../keyFigure/formula/NaFormulaNode.js";

/**
 * Formula node referencing an item in a report template.
 *
 * @param {string} spec.referenceId - Id of the item the node refers to.
 */
export default class ReportReferenceFormulaNode extends AbstractFormulaNode {
	constructor({ referenceId = "" } = {}) {
		super(...arguments);
		this._referenceId = referenceId;
		this._reference = null;
	}

	getReferenceId() {
		return this._referenceId;
	}

	getReference() {
		return this._reference;
	}

	getReferenceLabel() {
		const reference = this.getReference();

		return reference ? reference.getLabel() : this._referenceId;
	}

	/**
	 * Find the report item in the template.
	 */
	resolveReferences(reportTemplate) {
		this._reference = reportTemplate.getItemById(this._referenceId);
	}

	/**
	 * Expand the receiver into a formula node with no template item
	 * reference.
	 *
	 * To do so, the item is looked-up in the report.
	 */
	expandFormula(report) {
		if (!this._hasValidReference()) {
			return new NaFormulaNode();
		}

		const reference = report.findItemWithTemplate(this._reference);
		return reference.asFormulaNode();
	}

	validate({ collector }) {
		super.validate(...arguments);

		collector.addErrorIf(
			!this._hasValidReference(),
			_("The formula references an invalid {item}", {
				item: this.getReferenceLabel().toLowerCase(),
			}),
		);
	}

	hasReferenceTo(item) {
		return this.getReference() === item;
	}

	accept(visitor) {
		return visitor.visitReportReferenceNode(this);
	}

	getLabel() {
		const reference = this.getReference();
		return reference ? reference.getTitle() : this._referenceId;
	}

	add(node) {
		return new node.constructor({
			leftOperand: this,
			rightOperand: new NaFormulaNode(),
		});
	}

	addToBinaryOperator(node) {
		return new node.constructor({
			leftOperand: node.getLeftOperand(),
			rightOperand: this,
		});
	}

	addToSum(node) {
		return new node.constructor({
			operands: node.getOperands().slice(0, -1).concat(this),
		});
	}

	removeLastNode() {
		return new NaFormulaNode();
	}

	removeLastNodeFrom(node) {
		return new node.constructor({
			leftOperand: node.getLeftOperand(),
			rightOperand: new NaFormulaNode(),
		});
	}

	_hasValidReference() {
		const reference = this.getReference();

		return reference && reference.isValidFormulaReference();
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.reference = this._referenceId;

		return properties;
	}

	static getType() {
		return "ReportReferenceNode";
	}

	static transformMaterializationData(data) {
		super.transformMaterializationData(...arguments);

		data.referenceId = data.reference;
	}
}
